<template>
  <div>
    <navbar :links="links" />
    <div
      :class="{
        ...$desktopModeStyles,
        'elevation-1': scrollTop > fixedSearchThreshold,
        tools: scrollTop <= fixedSearchThreshold,
        'tools-fixed': scrollTop > fixedSearchThreshold,
      }"
    >
      <v-layout>
        <!-- Search -->
        <v-text-field
          ref="searchbar"
          v-model="searchText"
          class="searchbar-input"
          style="margin-left: auto"
          single-line
          hide-details
          :label="searchLabel"
          clearable
          append-icon="search"
          @click:append="focusSearch()"
          @click:clear="clearSearch()"
          @keyup.native.esc="clearSearch()"
        />
        <div style="flex-grow: 0; margin-left: auto">
          <v-checkbox
            v-model="allSelected"
            class="msr--text ml-3"
            color="msr"
            hide-details
          />
        </div>
      </v-layout>
    </div>
    <div
      v-if="scrollTop > fixedSearchThreshold"
      class="tools-fixed-spacer"
    />
    <v-list v-if="!loading && decoders && !filteredStoreDecoderWrappers.length">
      <v-subheader>{{ $t('noAvailableDecoders') }}</v-subheader>
    </v-list>
    <!-- Decoder list -->
    <selectable-store-decoders
      v-for="(wrapper, wrapperIndex) in filteredStoreDecoderWrappers"
      ref="selectableStoreDecoders"
      :key="wrapper.store.id"
      :store="wrapper.store"
      :decoders="wrapper.decoders"
      :preselect="decoders.length === 1"
      :show-divider="wrapperIndex < filteredStoreDecoderWrappers.length - 1"
    />
    <!-- Empty placeholder to allow a bit more scrolling -->
    <div style="height: 100px" />
    <!-- Add selected Decoders button -->
    <v-slide-y-reverse-transition>
      <v-btn
        v-if="atLeastOneDecoderSelected && !showConfirmationDialog"
        dark
        round
        fixed
        bottom
        right
        data-cy="continue-btn"
        :class="$desktopModeStyles"
        class="msr z-index-1"
        @click.stop="showConfirmationDialog = true"
      >
        <v-icon left>
          arrow_forward
        </v-icon>
        {{ $t('goForward') }}
      </v-btn>
    </v-slide-y-reverse-transition>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- Confirmation Dialog -->
    <entry-creation-dialog
      ref="confirmationDialog"
      :show.sync="showConfirmationDialog"
      :title="dialogTitle"
      :busy="adding"
      :allow-fullscreen="false"
      :show-hotrotation="false"
      :show-timeframe-controls="false"
      :show-weekday-options="false"
      @aborted="handleDialogAbortion()"
      @confirmed="handleDialogConfirmation()"
    />
  </div>
</template>

<script>
import addDecoderTo from '@/mixins/addDecoderTo.js';
import trackScrollTop from '@/mixins/trackScrollTop.js';

import EntryCreationDialog from '@/components/EntryCreationDialog';
import Navbar from '@/components/Navbar';
import SelectableStoreDecoders from '@/components/SelectableStoreDecoders';

export default {
  components: {
    EntryCreationDialog,
    Navbar,
    SelectableStoreDecoders,
  },
  mixins: [addDecoderTo, trackScrollTop],
  data() {
    return {
      decoders: [],
      links: ['contents', 'addDecoderToTicker'],
      ticker: null,
    };
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    /**
     * Get Ticker description and insert a link to it.
     */
    updateNavbarLinks(ticker) {
      let link = {
        label: ticker.message,
        path: '/ticker/' + ticker.id,
      };

      let lastLink = {
        label: 'addDecoderToTicker',
        active: false,
      };
      this.links = ['contents', link, lastLink];
    },
    handleDialogConfirmation() {
      let decoders = this.getSelectedDecoders();
      this.makeDecodersUseTicker(decoders);
    },
    makeDecodersUseTicker(decoders) {
      const vm = this;

      // FIXME: Make this one request instead of multiple.
      const tickerId = Number(this.$route.params.tickerId);

      const dialogOptions = this.$refs.confirmationDialog.options;
      const startDate = dialogOptions.expandDateControls
        ? this.$refs.confirmationDialog.startDate
        : '';
      const stopDate = dialogOptions.expandDateControls
        ? this.$refs.confirmationDialog.stopDate
        : '';
      const opts = {
        startdate: startDate,
        stopdate: stopDate,
      };

      vm.adding = true;
      const promises = [];
      for (var i = decoders.length - 1; i >= 0; i--) {
        const decoder = decoders[i];
        const promise = this.$api.addTickerToTVDecoder(
          decoder.id,
          tickerId,
          opts
        );
        promises.push(promise);
      }
      Promise.all(promises)
        .then((responses) => {
          vm.reset();
          vm.handleDialogAbortion();

          vm.$bus.$emit('alert-success', {
            message: 'tickersAddedSuccess',
            translate: true,
          });
          // Go back to Ticker which we added this to.
          vm.$store.commit('UNSET_PRE_ROUTE_QUESTION');
          vm.goToTicker(vm.$route.params.tickerId);
        })
        .finally(() => {
          vm.adding = false;
        });
    },
    goToTicker(tickerId) {
      let path = '/ticker/' + tickerId;
      this.$router.push(path);
    },
    fetchTicker() {
      const promise = this.$api.retrieveUserTicker(this.$route.params.tickerId);
      promise.then((response) => {
        this.ticker = response.data;
      });
      return promise;
    },
    fetchUserTVDecodersForTicker(ticker) {
      const promise = this.$api.listUserTVDecodersForTicker(ticker.id);
      promise.then((response) => {
        this.decoders = response.data;
      });
      return promise;
    },
    loadInitialData() {
      this.loading = true;
      this.fetchTicker().then(() => {
        this.updateNavbarLinks(this.ticker);
        this.fetchUserTVDecodersForTicker(this.ticker).then(() => {
          this.loading = false;
          this.handleDecoderPreselectionViaQuery();
        });
      });
    },
    handleDecoderPreselectionViaQuery() {
      if (this.$route.query.preselect) {
        const preselectedDecoderId = this.$route.query.preselect;
        this.$nextTick(() => {
          for (let widget of this.$refs.selectableStoreDecoders) {
            widget.preselectDecoderById(preselectedDecoderId);
          }
          this.showConfirmationDialog = true;
        });
      }
    },
  },
};
</script>

<style src="@/mixins/addDecoderTo.global.css"></style>

<style scoped src="@/mixins/addDecoderTo.scoped.css"></style>



// WEBPACK FOOTER //
// src/pages/AddDecoderToTicker.vue